import { DatalayerService } from '@analytics/datalayer';
import { AnalyticsActionTypes, type ToolTrackValues } from '@analytics/models';
import { ToolTrackingUtils } from '@analytics/utils';
import type { OnInit } from '@angular/core';
import { Directive, inject, Input } from '@angular/core';
import type { RichTextField, TextField } from '@core/jss-proxy';
import { sanitizeFieldValue } from '@core/jss-utils';

/**
 * Configuration interface for error tracking.
 */
interface TrackErrorModel extends ToolTrackValues {
  /** Type of analytics action for the error */
  type: AnalyticsActionTypes;

  /** The field where the error occurred */
  field: TextField | RichTextField | string;

  /** Description of the error */
  description: TextField | RichTextField | string;
}

/**
 * Angular directive for tracking system and tool errors.
 * This directive sends analytics events when errors occur in the application.
 *
 * Usage:
 * ```html
 * <div [wlTrackError]="{
 *   field: 'loginForm',
 *   description: 'Authentication failed',
 *   type: AnalyticsActionTypes.SYSTEM_ERROR,
 *   step: 1
 * }">
 * ```
 */
@Directive({
  selector: '[wlTrackError]',
  standalone: true,
})
export class TrackErrorDirective implements OnInit {
  readonly #datalayer = inject(DatalayerService);
  readonly #toolTrackingUtils = inject(ToolTrackingUtils);

  /**
   * Default configuration values for error tracking.
   */
  readonly #defaultValues: TrackErrorModel = {
    // Default error type is system error
    type: AnalyticsActionTypes.SYSTEM_ERROR,
    field: '',
    description: '',
    step: 0,
  };

  /**
   * Input binding for error tracking configuration
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input('wlTrackError') trackError: TrackErrorModel = this.#defaultValues;

  async ngOnInit() {
    const values = this.#getValues();
    const event = await this.#createErrorEvent(values);
    this.#datalayer.pushEvent(event);
  }

  /**
   * Gets the merged configuration values (defaults + user provided)
   */
  #getValues(): TrackErrorModel {
    return {
      ...this.#defaultValues,
      ...this.trackError,
    };
  }

  /**
   * Creates an error analytics event with the given parameters
   */
  async #createErrorEvent(values: TrackErrorModel) {
    const { description, field, type } = values;

    return this.#toolTrackingUtils.createToolEvent(
      AnalyticsActionTypes.TOOL_ERROR,
      {
        fieldname: sanitizeFieldValue(description),
        message: sanitizeFieldValue(field),
        errorKind: type,
      },
      await this.#toolTrackingUtils.getToolMetadata(values)
    );
  }
}
